/**
 * 轮询工具集（前端）。
 *
 * 解决问题：
 *   - 浏览器后台标签仍按 setTimeout 节流（≥1s），但前端仍会发送请求 → 浪费带宽 / 增加服务端负载
 *   - 多用户同时轮询易形成「雷鸣群」 → 加 ±20% 随机抖动避免同帧扎堆
 *   - 单调递减固定间隔对长任务（>5min）太频繁 → 随时间指数退避
 */

export function isPageHidden() {
  return typeof document !== 'undefined' && document.hidden;
}

/**
 * 在轮询间隔基础上加 ±jitterPct 的随机抖动（默认 ±20%）。
 * 例如 delayMs=2000, jitterPct=0.2 → [1600, 2400] 之间均匀分布。
 * 用于多客户端轮询错峰，避免同一秒并发命中服务端。
 */
export function jitterDelay(delayMs, jitterPct = 0.2) {
  const base = Math.max(0, Number(delayMs) || 0);
  if (base <= 0) return base;
  const span = base * jitterPct;
  const delta = (Math.random() * 2 - 1) * span;
  return Math.max(50, Math.round(base + delta));
}

/**
 * 指数退避：每轮成功后间隔放大（max=capMs）。失败 / 状态不变时保持 baseMs。
 *   - backoffFactor=1.5 → 2000 → 3000 → 4500 → 6750 → ... 到 cap 封顶
 */
export function expBackoff(prevMs, baseMs, capMs, backoffFactor = 1.5) {
  const base = Math.max(200, Number(baseMs) || 0);
  const cap = Math.max(base, Number(capMs) || base);
  const prev = Math.max(base, Number(prevMs) || base);
  const next = Math.round(prev * backoffFactor);
  return Math.min(cap, Math.max(base, next));
}

/**
 * 轮询专用 setTimeout 包装：后台 tab 时不调度实际请求（保留 last-firing 时间戳，
 * 切回前台后由调用方自己决定补一轮）。
 *
 * 用法：const t = setPollingTimeout(() => doPoll(), 2000);
 *       clearPollingTimeout(t);
 */
export function setPollingTimeout(fn, delayMs, opts = {}) {
  if (isPageHidden() && !(opts && opts.forceWhileHidden)) {
    return null;
  }
  const jitter = (opts && opts.jitter !== false);
  const d = jitter ? jitterDelay(delayMs, opts.jitterPct) : Math.max(0, Number(delayMs) || 0);
  return setTimeout(fn, d);
}

export function clearPollingTimeout(timer) {
  if (timer) {
    clearTimeout(timer);
  }
}
